// The codex (decision 0060): entries in sections the game names (a bestiary, lore, collections), filled by records
// through rules as data. A rule turns a record of kind `on` (matching `match`) into a mark on an entry (`entry` is a
// template over the record's fields: `"${kind}"` for an enemy's kind); an entry is found the first time any mark
// lands on it, and keeps a count per mark ("met" 3, "defeated" 2). The state is JSON and merges without a conflict
// (found is a union, each count the larger). Facts for the game's condition language: `codex.<section>.found`,
// `.total`, `.complete`, `codex.entry.<id>`.
import { fill, matches, type Match, type MetaFactSource, type MetaValue } from "./rules";

/** An entry in a section. Its words are the game's (string ids it keeps beside the id). */
export interface CodexEntryDef {
  id: string;
  section: string;
}

/** A rule: a record of kind `on` (matching `match`) puts `mark` on the entry `entry` names (a template). */
export interface CodexRule {
  on: string;
  match?: Match;
  entry: string;
  mark: string;
}

/** The saved state: each found entry's place in the order found (from 1) and its marks. */
export interface CodexState {
  found: Record<string, number>;
  marks: Record<string, Record<string, number>>;
}

/** One entry as the screens read it. */
export interface CodexEntryView {
  id: string;
  section: string;
  found: boolean;
  order: number | null;
  marks: Record<string, number>;
}

/** A fresh state. */
export const emptyCodex = (): CodexState => ({ found: {}, marks: {} });

/** Merge two states: found is a union (the first's order kept, the second's after), each mark count the larger. */
export function mergeCodex(a: CodexState, b: CodexState): CodexState {
  const out = structuredClone(a);
  const next = Object.values(out.found).reduce((m, v) => Math.max(m, v), 0);
  Object.entries(b.found)
    .filter(([id]) => !(id in out.found))
    .sort((x, y) => x[1] - y[1])
    .forEach(([id], i) => (out.found[id] = next + i + 1));
  for (const [id, ms] of Object.entries(b.marks)) {
    const m = (out.marks[id] ??= {});
    for (const [k, v] of Object.entries(ms)) m[k] = Math.max(m[k] ?? 0, v);
  }
  return out;
}

/** Problems with the entries and rules (empty when sound): duplicate ids, a fixed rule entry that doesn't exist. */
export function checkCodex(entries: readonly CodexEntryDef[], rules: readonly CodexRule[]): string[] {
  const out: string[] = [];
  const ids = new Set<string>();
  for (const e of entries) {
    if (ids.has(e.id)) out.push(`codex entry "${e.id}" is defined twice`);
    ids.add(e.id);
  }
  for (const r of rules) if (!r.entry.includes("${") && !ids.has(r.entry)) out.push(`codex rule on "${r.on}" names no entry "${r.entry}"`);
  return out;
}

/** The codex. */
export class Codex {
  private s: CodexState = emptyCodex();
  private readonly byId = new Map<string, CodexEntryDef>();
  private readonly listeners: ((id: string) => void)[] = [];

  constructor(readonly entries: readonly CodexEntryDef[], readonly rules: readonly CodexRule[] = []) {
    const bad = checkCodex(entries, rules);
    if (bad.length) throw new Error(`codex: ${bad.join("; ")}`);
    for (const e of entries) this.byId.set(e.id, e);
  }

  /** Call `fn` with an entry's id the first time it is found. Returns the remover. */
  onFound(fn: (id: string) => void): () => void {
    this.listeners.push(fn);
    return () => void this.listeners.splice(this.listeners.indexOf(fn), 1);
  }

  /** Feed a record through the rules; returns the entries found by it. Entries a template names that don't exist are
   *  skipped (a record about something the codex doesn't list). */
  record(kind: string, data: Readonly<Record<string, MetaValue>> = {}): string[] {
    const out: string[] = [];
    for (const r of this.rules) {
      if (r.on !== kind || !matches(data, r.match)) continue;
      const id = fill(r.entry, data);
      if (id !== null && this.mark(id, r.mark)) out.push(id);
    }
    return out;
  }

  /** Put a mark on an entry; true when that found it. Unknown entries are ignored (false). */
  mark(id: string, mark: string, n = 1): boolean {
    if (!this.byId.has(id)) return false;
    const m = (this.s.marks[id] ??= {});
    m[mark] = (m[mark] ?? 0) + n;
    if (id in this.s.found) return false;
    this.s.found[id] = Object.keys(this.s.found).length + 1;
    for (const l of [...this.listeners]) l(id);
    return true;
  }

  /** One entry. */
  entry(id: string): CodexEntryView {
    const e = this.byId.get(id);
    if (!e) throw new Error(`codex: no entry "${id}"`);
    return { id, section: e.section, found: id in this.s.found, order: this.s.found[id] ?? null, marks: { ...(this.s.marks[id] ?? {}) } };
  }

  /** A section's entries in the definitions' order. */
  section(section: string): CodexEntryView[] {
    return this.entries.filter((e) => e.section === section).map((e) => this.entry(e.id));
  }

  /** The sections, in the order their first entries come. */
  sections(): string[] {
    return [...new Set(this.entries.map((e) => e.section))];
  }

  /** How many of a section's entries are found. */
  count(section: string): { found: number; total: number } {
    const es = this.section(section);
    return { found: es.filter((e) => e.found).length, total: es.length };
  }

  snapshot(): CodexState {
    return structuredClone(this.s);
  }

  restore(s: CodexState): void {
    this.s = structuredClone({ ...emptyCodex(), ...s });
  }

  /** Merge another state into this one; returns the entries newly found here. */
  merge(other: CodexState): string[] {
    const before = new Set(Object.keys(this.s.found));
    this.s = mergeCodex(this.s, other);
    return Object.keys(this.s.found).filter((id) => !before.has(id));
  }

  /** Facts for the game's condition language. */
  facts(name = "codex"): MetaFactSource {
    const sections = new Set(this.sections());
    return {
      name,
      get: (k) => {
        if (k.startsWith("entry.")) return k.slice(6) in this.s.found;
        const i = k.lastIndexOf(".");
        const sec = k.slice(0, i);
        const c = this.count(sec);
        const what = k.slice(i + 1);
        return what === "found" ? c.found : what === "total" ? c.total : c.found === c.total && c.total > 0;
      },
      knows: (k) => {
        if (k.startsWith("entry.")) return this.byId.has(k.slice(6));
        const i = k.lastIndexOf(".");
        return i > 0 && sections.has(k.slice(0, i)) && ["found", "total", "complete"].includes(k.slice(i + 1));
      },
      keys: () => [...[...sections].flatMap((s) => [`${s}.found`, `${s}.total`, `${s}.complete`]), ...[...this.byId.keys()].map((id) => `entry.${id}`)],
    };
  }
}
